import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, CheckCircle2, Mail, MapPin, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import UserNavbar from "../../components/UserNavbar";

export default function EditProfile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BASE_URL}profile`,
          { headers: authHeaders() }
        );
        const user = data?.data || {};
        setFullName(user.full_name || "");
        setEmail(user.email || "");
        setAddress(user.address || "");
      } catch (error) {
        console.error("Unable to load profile", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim() || !address.trim()) {
      setErrorMessage("Please fill name, email and address.");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        full_name: fullName.trim(),
        email: email.trim(),
        address: address.trim(),
      };

      await axios.put(
        `${process.env.REACT_APP_BASE_URL}profile`,
        payload,
        { headers: authHeaders() }
      );

      navigate("/profile");
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        navigate("/login", { state: { from: { pathname: "/edit-profile" } } });
        return;
      }
      setErrorMessage(error?.response?.data?.message || "Unable to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <UserNavbar />
      <main className="min-h-screen bg-violet-50 pt-40 px-4 pb-10">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-11 h-11 rounded-full bg-white shadow-sm flex items-center justify-center"
              aria-label="Go back"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Edit Profile</h1>
              <p className="text-slate-500">Update your personal information.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-sm p-6 md:p-8">
            {errorMessage && <p className="mb-4 text-sm text-red-600">{errorMessage}</p>}
            {loading ? (
              <div className="py-16 text-center text-slate-500">Loading profile...</div>
            ) : (
              <div className="space-y-5">
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Full Name</span>
                  <div className="mt-2 flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3">
                    <User size={19} className="text-slate-400" />
                    <input
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full outline-none"
                      placeholder="Enter your full name"
                    />
                  </div>
                </label>

                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Email</span>
                  <div className="mt-2 flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3">
                    <Mail size={19} className="text-slate-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full outline-none"
                      placeholder="Enter your email"
                    />
                  </div>
                </label>

                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Address</span>
                  <div className="mt-2 flex items-start gap-3 rounded-2xl border border-slate-200 px-4 py-3">
                    <MapPin size={19} className="text-slate-400 mt-1" />
                    <textarea
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="w-full min-h-28 resize-y outline-none"
                      placeholder="Enter your address"
                    />
                  </div>
                </label>

                <button
                  type="submit"
                  disabled={saving}
                  className="w-full rounded-2xl bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white py-4 font-semibold flex items-center justify-center gap-2"
                >
                  <CheckCircle2 size={20} />
                  {saving ? "Updating..." : "Update Profile"}
                </button>
              </div>
            )}
          </form>
        </div>
      </main>
    </>
  );
}
